import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  LoaderCircle,
  Search,
  ChevronDown,
  ChevronRight,
  EyeOff,
  Copy,
  ExternalLink,
  Link2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useProject } from '@/lib/ProjectContext';
import { useDashboardCache } from '@/lib/DashboardCacheContext';
import { usePermissions } from '@/lib/usePermissions';
import { useSubmissionFilter } from '@/lib/useSubmissionFilter';
import { useScanStatus } from '@/lib/ScanStatusContext';
import { invalidateConsistency, onConsistencyInvalidated } from '@/lib/consistencyInvalidation';

import { Telemetry } from '@/lib/telemetry';
import { startTimer, endTimer } from '@/lib/telemetry-timers';
import { timeAgo } from '@/lib/timeAgo';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import { OverrideHistory } from '@/components/shared/OverrideHistory';
import type { CrossReferenceScanResponse, CrossReferenceEntry, ScanIntegrityWarning } from '@/types/api';
import { DataIntegrityNotice } from '@/components/shared/DataIntegrityNotice';
import { TierUncertaintyIndicator } from '@/components/shared/TierUncertaintyIndicator';
import type { FindingTier } from '@/lib/findingClassification';
import { countByScope } from '@/lib/findingClassification';
import { ScopePill } from '@/components/shared/ScopePill';
import { ScopeFilterSelect, applyScopeFilter, type ScopeFilter } from '@/components/shared/ScopeFilterSelect';
import { ScopeBreakdownCard } from '@/components/shared/ScopeBreakdownCard';
import { SummaryStatCard } from '@/components/shared/SummaryStatCard';

type UIState = 'loading' | 'landing' | 'scanning' | 'results';
type StatusFilter = 'all' | 'valid' | 'missing';
type TieredEntry = CrossReferenceEntry & { tier: FindingTier };

const PAGE_SIZE = 10;

export function CrossReferencesPage() {
  const tz = useDisplayTimezone();
  const { activeProject } = useProject();
  const { invalidate: invalidateDashboard } = useDashboardCache();
  const { canRunScans } = usePermissions();
  const { submissionId, submissions, isFiltering, classifyTier } = useSubmissionFilter();
  const [overlayApplicable, setOverlayApplicable] = useState(false);
  const [uiState, setUiState] = useState<UIState>('loading');
  const [data, setData] = useState<CrossReferenceScanResponse | null>(null);
  const [integrityWarnings, setIntegrityWarnings] = useState<ScanIntegrityWarning[]>([]);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [hideDismissed, setHideDismissed] = useState(() => {
    const stored = sessionStorage.getItem('chrona_xref_hideDismissed');
    return stored !== null ? stored === 'true' : true;
  });
  const [excludeModuleRefs, setExcludeModuleRefs] = useState(() => {
    return sessionStorage.getItem('chrona_xref_excludeModuleRefs') === 'true';
  });
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(isFiltering ? 'this-sub' : 'all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [toast, setToast] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  // True during the synchronous window around invalidateConsistency() so
  // loadCached can suppress the spurious review_results_viewed that would
  // otherwise fire via the self-invalidation path after every scan. See A2F-365.
  const scanRefetchRef = useRef(false);
  const scanStatus = useScanStatus();
  const scanBusy = scanStatus.isScanning('cross-references');

  const projectId = activeProject?.id;

  const dismissedCount = useMemo(
    () => data?.references.filter((r) => r.dismissed).length ?? 0,
    [data],
  );

  // A2F-462 follow-up: read via aggregate endpoint.
  const loadCached = useCallback(() => {
    if (!projectId) return;
    // Capture synchronously: true only when called from our own scan's
    // invalidateConsistency() self-trigger (see scanRefetchRef usage in startScan).
    const isScanRefetch = scanRefetchRef.current;
    setUiState('loading');
    setError('');
    api.consistency.get(projectId, submissionId ?? undefined)
      .then((result) => {
        setOverlayApplicable(result.overlayApplicable);
        // A2F-832: capture warnings for cross_references_missing.
        setIntegrityWarnings(
          (result.dataIntegrityWarnings ?? []).filter(
            (w) => w.featureId === 'cross_references_missing',
          ),
        );
        const val = result.perFeature?.crossReferencesMissing?.value as CrossReferenceScanResponse | null | undefined;
        if (val) {
          setData(val);
          setUiState('results');
          // Only fire on genuine cache loads (mount, cross-page invalidation).
          // Suppress on the post-scan self-invalidation path — that path is
          // already covered by review_scan_completed (A2F-365 REVIEW finding).
          if (!isScanRefetch) {
            Telemetry.reviewResultsViewed({
              feature: 'cross_references',
              finding_count: val.references.length,
              stale_files: val.staleFiles ?? 0,
            });
          }
        } else {
          setUiState('landing');
        }
      })
      .catch(() => {
        setUiState('landing');
      });
  }, [projectId, submissionId]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  useEffect(() => {
    return onConsistencyInvalidated((pid) => {
      if (pid === projectId) loadCached();
    });
  }, [projectId, loadCached]);

  const startScan = useCallback(() => {
    if (!projectId) return;
    if (!scanStatus.startScan('cross-references')) return;
    const overlayId = overlayApplicable ? (submissionId ?? undefined) : undefined;
    setUiState('scanning');
    setError('');
    const controller = new AbortController();
    abortRef.current = controller;

    startTimer('scan_cross_references');
    Telemetry.reviewScanStarted({ feature: 'cross_references', file_count: 0 });

    api.crossReferences
      .scan(projectId, controller.signal, overlayId)
      .then((result) => {
        setData(result);
        setUiState('results');
        Telemetry.reviewScanCompleted({
          feature: 'cross_references',
          file_count: result.summary.scannedFiles,
          finding_count: result.summary.missing, // ⚠️ CrossReferenceSummary has `missing`, not `inconsistent`
          duration_ms: endTimer('scan_cross_references'),
        });
        invalidateDashboard();
        scanRefetchRef.current = true;
        invalidateConsistency(projectId); // triggers loadCached synchronously; ref captured there
        scanRefetchRef.current = false;
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          setUiState(data ? 'results' : 'landing');
          return;
        }
        const msg = err.message || 'Scan failed';
        setError(msg);
        setUiState(data ? 'results' : 'landing');
        Telemetry.reviewScanFailed({ feature: 'cross_references', error_message: msg });
        Telemetry.errorSwallowed({ error_type: 'scan_failure', operation: 'cross_references_scan', screen_name: '/review/cross-references', error_message: msg });
      })
      .finally(() => {
        abortRef.current = null;
        scanStatus.endScan('cross-references');
      });
  }, [projectId, data, overlayApplicable, submissionId]);

  const cancelScan = () => {
    abortRef.current?.abort();
  };

  const rescan = () => {
    if (!projectId) return;
    Telemetry.reviewCacheCleared({ feature: 'cross_references' });
    api.crossReferences.clearCache(projectId).then(() => startScan());
  };

  const toggleRow = (sectionNumber: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(sectionNumber)) next.delete(sectionNumber);
      else next.add(sectionNumber);
      return next;
    });
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleOpenDoc = useCallback(
    async (docName: string, referenceText: string) => {
      if (!data) return;
      const docId = data.documentMap[docName];
      try {
        await navigator.clipboard.writeText(referenceText);
      } catch {
        // Clipboard may fail in non-secure contexts
      }
      if (docId) {
        api.documents.openLocal(docId).catch(() => {});
      }
      showToast(`Copied "${referenceText}" to clipboard — use Ctrl+F to find`);
    },
    [data],
  );

  const handleDismiss = useCallback(
    async (sectionNumber: string, reason: string) => {
      if (!projectId || !data) return;
      await api.crossReferences.dismiss(projectId, sectionNumber, reason);
      Telemetry.findingDismissed({ feature: 'cross_references', finding_id: sectionNumber, has_rationale: reason.trim().length >= 10 });
      setData({
        ...data,
        references: data.references.map((r) =>
          r.sectionNumber === sectionNumber
            ? { ...r, dismissed: true, dismissReason: reason || '' }
            : r,
        ),
      });
    },
    [projectId, data],
  );

  const handleUndismiss = useCallback(
    async (sectionNumber: string) => {
      if (!projectId || !data) return;
      await api.crossReferences.undismiss(projectId, sectionNumber);
      Telemetry.findingUndismissed({ feature: 'cross_references', finding_id: sectionNumber });
      setData({
        ...data,
        references: data.references.map((r) =>
          r.sectionNumber === sectionNumber
            ? { ...r, dismissed: false, dismissReason: null }
            : r,
        ),
      });
    },
    [projectId, data],
  );

  // Filter + search
  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.references;
    if (hideDismissed) {
      items = items.filter((r) => !r.dismissed);
    }
    if (excludeModuleRefs) {
      // Hide single-number references (top-level module refs like "Module 3")
      items = items.filter((r) => r.sectionNumber.includes('.'));
    }
    if (statusFilter === 'valid') {
      items = items.filter((r) => r.status === 'valid');
    } else if (statusFilter === 'missing') {
      items = items.filter((r) => r.status === 'missing');
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      items = items.filter(
        (r) =>
          r.sectionNumber.toLowerCase().includes(q) ||
          r.documents.some((d) => d.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [data, statusFilter, searchText, hideDismissed, excludeModuleRefs]);

  // A2F-474: Classify findings into three tiers + sort
  const tieredItems = useMemo((): TieredEntry[] => {
    if (!isFiltering || !data) return filtered.map(f => ({ ...f, tier: 'new' as const }));

    const tierOrder: Record<FindingTier, number> = { new: 0, 'pre-existing': 1, inherited: 2 };
    const statusOrder: Record<string, number> = { missing: 0, valid: 1 };

    const items: TieredEntry[] = filtered.map(item => ({
      ...item,
      tier: classifyTier(item, data.documentMap),
    }));

    if (scopeFilter === 'in-scope' || scopeFilter === 'this-sub') {
      items.sort((a, b) =>
        tierOrder[a.tier] - tierOrder[b.tier]
        || (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4)
      );
    } else {
      items.sort((a, b) => (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4));
    }
    return items;
  }, [filtered, isFiltering, data, classifyTier, scopeFilter]);

  const tierCounts = useMemo(() => {
    if (!isFiltering) return { new: 0, preExisting: 0, inherited: 0 };
    return {
      new: tieredItems.filter(i => i.tier === 'new').length,
      preExisting: tieredItems.filter(i => i.tier === 'pre-existing').length,
      inherited: tieredItems.filter(i => i.tier === 'inherited').length,
    };
  }, [tieredItems, isFiltering]);

  const displayItems = useMemo(() => {
    if (!isFiltering) return tieredItems;
    return applyScopeFilter(tieredItems, scopeFilter);
  }, [tieredItems, isFiltering, scopeFilter]);

  // A2F-487: per-card 3-bucket breakdown (see AbbreviationsPage for rationale)
  const cardCounts = useMemo(() => {
    if (!isFiltering) return null;
    return {
      total: countByScope(tieredItems),
      valid: countByScope(tieredItems.filter(r => r.status === 'valid')),
      missing: countByScope(tieredItems.filter(r => r.status === 'missing' && !r.dismissed)),
    };
  }, [isFiltering, tieredItems]);

  // A2F-488: tile-click filter shortcuts (see AbbreviationsPage)
  const handleTileFilter = useCallback((status: string, scope: ScopeFilter) => {
    setStatusFilter(status as StatusFilter);
    setScopeFilter(scope);
    Telemetry.reviewTileFilterClicked({ page: 'cross-references', status, scope });
  }, []);

  const handleStatusOnlyFilter = useCallback((status: string) => {
    setStatusFilter(status as StatusFilter);
    Telemetry.reviewTileFilterClicked({ page: 'cross-references', status, scope: 'all' });
  }, []);

  const totalPages = Math.max(1, Math.ceil(displayItems.length / PAGE_SIZE));
  const pageItems = displayItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Persist filter toggles to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('chrona_xref_hideDismissed', String(hideDismissed));
  }, [hideDismissed]);
  useEffect(() => {
    sessionStorage.setItem('chrona_xref_excludeModuleRefs', String(excludeModuleRefs));
  }, [excludeModuleRefs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, searchText, hideDismissed, excludeModuleRefs, scopeFilter]);

  if (!activeProject) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-400">
            Select a project to scan cross-references.
          </p>
        </div>
      </div>
    );
  }

  // --- Loading ---
  if (uiState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <LoaderCircle className="w-6 h-6 text-brand-orange animate-spin" />
        <p className="text-sm text-slate-500">Checking for cached results...</p>
      </div>
    );
  }

  // --- Scanning ---
  if (uiState === 'scanning') {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-4">
        <LoaderCircle className="w-8 h-8 text-brand-orange animate-spin mx-auto" />
        <p className="text-sm text-slate-600">
          Scanning documents for cross-references...
        </p>
        <p className="text-xs text-slate-400">
          Extracting section references from your documents and validating against project index.
        </p>
        <button
          onClick={cancelScan}
          className="text-xs text-slate-500 hover:text-slate-700 underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  // --- Landing ---
  if (uiState === 'landing') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4">
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            Chrona Review
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal">
            Cross-References
          </h1>
          <p className="text-sm text-slate-500">
            Scan your documents for section cross-references (e.g.,
            "Section 3.2.P.8", "see Module 3") and validate them against
            your project's eCTD section index. Broken references pointing
            to non-existent sections are flagged.
          </p>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <button
            onClick={startScan}
            disabled={!canRunScans || scanBusy}
            title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {scanBusy ? 'Scan in progress\u2026' : 'Start Scan'}
          </button>
        </div>
      </div>
    );
  }

  // --- Results ---
  if (!data) return null;
  const { summary } = data;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-slate-800 text-white text-sm rounded-lg shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Chrona Review
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal">
          Cross-References
        </h1>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary cards — A2F-487/488: click-to-filter tiles.
          Files Scanned stays non-clickable (scan-stats metric). */}
      {isFiltering && cardCounts ? (
        <div className="grid grid-cols-4 gap-4">
          <ScopeBreakdownCard label="Total References" color="slate" {...cardCounts.total}
            statusValue="all" activeStatus={statusFilter} activeScope={scopeFilter} onFilter={handleTileFilter} />
          <ScopeBreakdownCard label="Valid" color="green" {...cardCounts.valid}
            statusValue="valid" activeStatus={statusFilter} activeScope={scopeFilter} onFilter={handleTileFilter} />
          <ScopeBreakdownCard label="Missing" color="orange" {...cardCounts.missing}
            statusValue="missing" activeStatus={statusFilter} activeScope={scopeFilter} onFilter={handleTileFilter} />
          <SummaryStatCard label="Files Scanned" value={summary.scannedFiles} color="slate" />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          <SummaryStatCard label="Total References" value={summary.totalReferences} color="slate"
            statusValue="all" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Valid" value={summary.valid} color="green"
            statusValue="valid" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Missing" value={summary.missing} color="orange"
            statusValue="missing" activeStatus={statusFilter} onFilter={handleStatusOnlyFilter} />
          <SummaryStatCard label="Files Scanned" value={summary.scannedFiles} color="slate" />
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search section numbers or documents..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="valid">Valid</option>
          <option value="missing">Missing</option>
        </select>
        {dismissedCount > 0 && (
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideDismissed}
              onChange={(e) => setHideDismissed(e.target.checked)}
              className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
            />
            <EyeOff className="w-3.5 h-3.5 text-slate-400" />
            Hide dismissed ({dismissedCount})
          </label>
        )}
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={excludeModuleRefs}
            onChange={(e) => setExcludeModuleRefs(e.target.checked)}
            className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
          />
          Exclude module refs
        </label>
        {isFiltering && (
          <ScopeFilterSelect
            value={scopeFilter}
            onChange={setScopeFilter}
            thisSubCount={tierCounts.new}
            preExistingCount={tierCounts.preExisting}
            inheritedCount={tierCounts.inherited}
          />
        )}
        <div className="ml-auto">
          <button
            onClick={rescan}
            disabled={!canRunScans || scanBusy}
            title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
            className={`px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {scanBusy ? 'Scan in progress\u2026' : 'Rescan'}
          </button>
        </div>
      </div>

      {/* A2F-832: scope-filter-independent integrity notice (BRIEF gotcha #9) */}
      {isFiltering && <DataIntegrityNotice warnings={integrityWarnings} />}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-8" />
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-8">
                Status
              </th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">
                Section
              </th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">
                Occurrences
              </th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">
                Documents
              </th>
              {isFiltering && (
                <th className="text-left px-4 py-3 font-medium text-slate-600">
                  Scope
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={isFiltering ? 6 : 5} className="px-4 py-8 text-center text-slate-400">
                  No cross-references match your filters.
                </td>
              </tr>
            )}
            {pageItems.map((entry) => (
              <CrossReferenceRow
                key={entry.sectionNumber}
                entry={entry}
                tier={isFiltering ? (entry as TieredEntry).tier : null}
                tierConfident={entry.tierConfident}
                expanded={expandedRows.has(entry.sectionNumber)}
                onToggle={() => toggleRow(entry.sectionNumber)}
                documentMap={data.documentMap}
                onDismiss={handleDismiss}
                onUndismiss={handleUndismiss}
                onOpenDoc={handleOpenDoc}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Showing {page * PAGE_SIZE + 1}&ndash;
            {Math.min((page + 1) * PAGE_SIZE, displayItems.length)} of{' '}
            {displayItems.length}
          </span>
          <div className="flex gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-slate-400 flex items-center gap-2">
        <span title={formatInstant(data.scannedAt, tz)}>Scanned {timeAgo(data.scannedAt, tz)}</span>
        <span>&middot;</span>
        <span>Regex-based detection</span>
        <span>&middot;</span>
        <span>
          {summary.scannedFiles} file{summary.scannedFiles !== 1 ? 's' : ''}
        </span>
        {(data.staleFiles ?? 0) > 0 && (
          <>
            <span>&middot;</span>
            <span className="text-orange-500 font-medium">
              {data.staleFiles} file{data.staleFiles !== 1 ? 's' : ''} changed since scan
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// --- Sub-components ---


function CrossReferenceRow({
  entry,
  tier,
  tierConfident,
  expanded,
  onToggle,
  documentMap,
  onDismiss,
  onUndismiss,
  onOpenDoc,
}: {
  entry: CrossReferenceEntry;
  tier: FindingTier | null;
  tierConfident?: boolean;
  expanded: boolean;
  onToggle: () => void;
  documentMap: Record<string, string>;
  onDismiss: (sectionNumber: string, reason: string) => Promise<void>;
  onUndismiss: (sectionNumber: string) => Promise<void>;
  onOpenDoc: (docName: string, referenceText: string) => void;
}) {
  const { canDismissFindings } = usePermissions();
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);

  const rowOpacity = entry.dismissed ? 'opacity-50' : '';

  const confirmDismiss = async () => {
    setBusy(true);
    try {
      await onDismiss(entry.sectionNumber, dismissReason);
      setDismissing(false);
      setDismissReason('');
    } finally {
      setBusy(false);
    }
  };

  const confirmUndismiss = async () => {
    setBusy(true);
    try {
      await onUndismiss(entry.sectionNumber);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr
        className={`border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer ${rowOpacity}`}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={entry.status} feature="cross_references" />
        </td>
        <td className="px-4 py-3 font-mono font-semibold text-brand-charcoal">
          {entry.sectionNumber}
        </td>
        <td className="px-4 py-3 text-slate-500">
          {entry.occurrenceCount} occurrence{entry.occurrenceCount !== 1 ? 's' : ''}
        </td>
        <td className="px-4 py-3 text-slate-500">{entry.documentsCount}</td>
        {tier && (
          <td className="px-4 py-3">
            <span className="inline-flex items-center gap-0.5">
              <ScopePill tier={tier} />
              {/* A2F-832: per-finding indicator when tier was conservatively assigned */}
              {tierConfident === false && <TierUncertaintyIndicator />}
            </span>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className={`bg-slate-50/30 ${rowOpacity}`}>
          <td colSpan={tier ? 6 : 5} className="px-8 py-4 space-y-3">
            {/* Target document (if valid) */}
            {entry.status === 'valid' && entry.targetDocument && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Target Document
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Link2 className="w-3.5 h-3.5 text-green-600" />
                  <button
                    className="text-brand-orange hover:underline text-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (entry.targetDocumentId) {
                        api.documents.openLocal(entry.targetDocumentId).catch(() => {});
                      }
                    }}
                  >
                    {entry.targetDocument}
                  </button>
                </div>
              </div>
            )}

            {/* Occurrences */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Occurrences ({entry.occurrenceCount})
              </p>
              <div className="space-y-2">
                {entry.occurrences.map((occ, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-2.5 bg-white rounded-lg border border-slate-100"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-400 mb-1">
                        {occ.document}
                      </p>
                      <p className="text-sm text-slate-700 font-mono break-words">
                        ...{occ.context}...
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-brand-orange hover:bg-orange-50 rounded transition-colors"
                        title="Open document and copy reference"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDoc(occ.document, occ.match);
                        }}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </button>
                      <button
                        className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                        title="Copy reference text"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(occ.match).catch(() => {});
                        }}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dismiss / Restore actions */}
            {entry.dismissed ? (
              <div className="pt-2 border-t border-slate-100 space-y-2">
                {entry.dismissReason && (
                  <p className="text-xs text-slate-500">
                    <span className="font-medium">Dismiss reason:</span>{' '}
                    {entry.dismissReason}
                  </p>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmUndismiss();
                  }}
                  disabled={busy || !canDismissFindings}
                  title={canDismissFindings ? undefined : 'Requires Contributor role'}
                  className={`px-3 py-1.5 text-xs font-medium text-brand-orange border border-brand-orange/30 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50 ${!canDismissFindings ? 'cursor-not-allowed' : ''}`}
                >
                  {busy ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            ) : dismissing ? (
              <div
                className="pt-2 border-t border-slate-100 space-y-2"
                onClick={(e) => e.stopPropagation()}
              >
                <textarea
                  placeholder="Reason for dismissal (required, minimum 10 characters)"
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange resize-none"
                />
                <p className={`text-xs mt-1 ${dismissReason.trim().length >= 10 ? 'text-green-500' : 'text-slate-400'}`}>
                  {dismissReason.trim().length}/10 characters
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={confirmDismiss}
                    disabled={busy || dismissReason.trim().length < 10}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                  >
                    {busy ? 'Dismissing...' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => {
                      setDismissing(false);
                      setDismissReason('');
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-2 border-t border-slate-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDismissing(true);
                  }}
                  disabled={!canDismissFindings}
                  title={canDismissFindings ? undefined : 'Requires Contributor role'}
                  className={`px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${!canDismissFindings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Dismiss
                </button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
